import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { useComplianceCaseContext } from "../../../entrypoints/ComplianceCaseContext";
import { ComplianceCaseTypeEnum } from "../../../graphql/supergraphEnums";
import { AccountTypeEnum } from "../../../graphql/supergraphTypes";
import { resolvedAccountType, isJoinAccountType } from "../../../utils/account_types";
import { isAmlProgram } from "../../../utils/compliance_program";
import { RuleEngineStatus } from "../../common/ProfileManagement/types";
import { DocumentStatusLabel } from "../../documents/DocumentStatusLabel";
import { completionStatus, shouldShow } from "../../ProfileActionOverview/utils";
import { ProfileStatusBadge } from "../../ProfileStatusBadge";
import { ProfileStatus } from "../../ProfileStatusBadge/types";
import { TreeProfile } from "../types";
import { provideLegalName, getComplianceLabel, getRelationshipLabel } from "../utils";
import {
  nodeContentWrapper,
  nodeContentWrapperClickable,
  leftContentWrapper,
  headerText,
  subHeaderContainer,
  subHeaderDivider,
  rightContentWrapper,
} from "./style_vanilla.css";
import { getProfileStatus } from "./utils";

export const ProfileTreeCard: React.FC<{
  profileInfo: TreeProfile;
  basePath: string;
  rootAccountType?: AccountTypeEnum | null;
  rootRulesEngine?: RuleEngineStatus | null;
  childOwnersCount?: number;
}> = ({ profileInfo, basePath, rootAccountType, rootRulesEngine, childOwnersCount }) => {
  const history = useHistory();
  const header = provideLegalName(profileInfo);
  const relationship = profileInfo.parentRelationships?.map((pr) => pr.relationshipType);
  const { isReadOnly, programId, rootProfileId, caseType, enableLPUXv2 } =
    useComplianceCaseContext();
  const resolvedProfileAccountType = resolvedAccountType(profileInfo);
  const complianceTypeLabel = getComplianceLabel(
    profileInfo.complianceObjectType,
    resolvedProfileAccountType,
  );
  const isRoot = profileInfo.id === rootProfileId;
  const relationshipLabel = relationship
    ?.map((relation) => {
      const label = getRelationshipLabel(relation);
      return label;
    })
    .filter(Boolean);

  const subHeader =
    relationshipLabel?.length > 0
      ? [...relationshipLabel, complianceTypeLabel].filter(Boolean)
      : complianceTypeLabel
        ? [complianceTypeLabel]
        : [];

  const rootProfile = useMemo(
    () => ({
      id: rootProfileId,
      accountType: rootAccountType,
      rulesEngine: rootRulesEngine,
    }),
    [rootProfileId, rootAccountType, rootRulesEngine],
  );

  const profileForStatus = useMemo(
    () => ({ ...profileInfo, childOwnersCount }),
    [profileInfo, childOwnersCount],
  );

  const completionCriteria = completionStatus(
    caseType,
    rootProfile,
    profileForStatus,
    programId,
  );

  const { showDetails } = shouldShow(caseType, rootProfile, profileForStatus, programId);

  const profileStatus = useMemo(() => {
    return getProfileStatus(
      caseType,
      rootProfile,
      completionCriteria,
      profileForStatus,
      programId,
    );
  }, [caseType, rootProfile, profileForStatus, programId, completionCriteria]);
  const isCompleted = profileStatus === ProfileStatus.Completed;
  const showDocumentStatus = !(
    isJoinAccountType(resolvedProfileAccountType) || profileStatus === ProfileStatus.NotStarted
  );
  const buttonText = isCompleted ? "Edit" : "Start";
  const buttonVariant = isCompleted ? "neutral" : "primary";

  const disableJointAccountAction =
    isJoinAccountType(resolvedProfileAccountType) &&
    caseType === ComplianceCaseTypeEnum.DocumentRefresh &&
    !showDetails;

  /**
   * Root profile should be always enabled,
   * Whereas other profiles should be editable only if they are not readonly.
   * AML is an exception, as we collect documents for AML programs.
   */
  const enableAction = isRoot || !isReadOnly || isAmlProgram(programId);
  const isClickable = enableLPUXv2 && enableAction && !disableJointAccountAction;

  const handleCardClick = () => {
    if (isClickable) {
      history.push(`${basePath}/overview/${profileInfo.id}`);
    }
  };

  const wrapperClassName = isClickable ? nodeContentWrapperClickable : nodeContentWrapper;

  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={Sizes.MD}
      className={wrapperClassName}
      onClick={handleCardClick}
    >
      <Flex flexDirection="column" className={leftContentWrapper}>
        <Text
          variant="strong-lg"
          className={headerText}
          data-testid="card_header"
          title={header}
        >
          {header}
        </Text>

        <Flex
          flexDirection="row"
          alignItems="end"
          gap={Sizes.XS}
          className={subHeaderContainer}
          data-testid="subheader_container"
        >
          {subHeader &&
            subHeader
              .filter((item): item is string => item !== undefined)
              .map((item, index) => (
                <React.Fragment key={`subheader-${index}`}>
                  {index > 0 && (
                    <Text
                      variant="secondary-sm"
                      className={subHeaderDivider}
                      data-testid="subheader_divider"
                    >
                      |
                    </Text>
                  )}
                  <Text variant="secondary-sm">{item}</Text>
                </React.Fragment>
              ))}
        </Flex>
      </Flex>
      {enableLPUXv2 ? (
        <Flex alignItems="center" justifyContent="center" gap="4">
          <ProfileStatusBadge status={profileStatus} />
          <Icon type="chevron-right" size="medium" />
        </Flex>
      ) : (
        <Flex
          flexDirection="row"
          alignItems="center"
          gap={Sizes.SM}
          className={rightContentWrapper}
        >
          {showDocumentStatus && (
            <DocumentStatusLabel documentTypes={profileInfo.rulesEngine?.documentTypes} />
          )}
          <Button
            variant={buttonVariant}
            data-testid="profile_tree_card_button"
            onClick={() => {
              history.push(`${basePath}/overview/${profileInfo.id}`);
            }}
            disabled={!enableAction}
          >
            {buttonText}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
